import {useSuspenseGetBatchesByIds} from '@/modules/production/api/use-get-batches-by-ids'
import {
  createPickingListBatchQuantityMap,
  mapPickingListAllocatedQuantityToBatches,
  mapPickingListBatchesIds,
} from '@/modules/production/functions'
import {useSuspenseGetRestVials} from '@/modules/stocks/rest-vials/api'
import {PickingListOrderRestVial, PickingListOrderReturn} from '@cypro/engine/database/schema'
import {Batch, PatientTherapySequence} from '@cypro/engine/schema'

export interface CleanroomRestVialEntry extends PickingListOrderRestVial {
  batch: Batch | undefined
  internalId: number
}

export type CleanroomReturnEntry = PickingListOrderReturn

export function useCleanroomSequenceBatches(sequence: PatientTherapySequence | null) {
  const sequenceOrder = sequence?.order?.distributedOrders?.orders?.find(o => o.sequenceId === sequence?.id)

  const [activeSubstances] = useSuspenseGetBatchesByIds(
    mapPickingListBatchesIds(sequenceOrder?.activeSubstances?.batches)
  )
  const [carrier] = useSuspenseGetBatchesByIds(mapPickingListBatchesIds(sequenceOrder?.carrier?.batches))
  const [consumables] = useSuspenseGetBatchesByIds(mapPickingListBatchesIds(sequenceOrder?.consumables?.batches))
  const [solvents] = useSuspenseGetBatchesByIds(mapPickingListBatchesIds(sequenceOrder?.solvents?.batches))

  const batches = {
    activeSubstances: mapPickingListAllocatedQuantityToBatches(
      activeSubstances,
      createPickingListBatchQuantityMap(sequenceOrder, 'activeSubstances'),
      sequenceOrder?.activeSubstances?.batches ?? []
    ),
    carrier: mapPickingListAllocatedQuantityToBatches(
      carrier,
      createPickingListBatchQuantityMap(sequenceOrder, 'carrier')
    ),
    consumables: mapPickingListAllocatedQuantityToBatches(
      consumables,
      createPickingListBatchQuantityMap(sequenceOrder, 'consumables')
    ),
    solvents: mapPickingListAllocatedQuantityToBatches(
      solvents,
      createPickingListBatchQuantityMap(sequenceOrder, 'solvents')
    ),
  }

  const [restVialBatches] = useSuspenseGetBatchesByIds(
    sequenceOrder?.restVials?.map(rv => rv.batchId).filter((id): id is string => Boolean(id)) || []
  )
  const {restVials} = useSuspenseGetRestVials({
    filters: {ids: sequenceOrder?.restVials?.map(r => r.restVialId)},
  })

  const restVialEntries: CleanroomRestVialEntry[] = (sequenceOrder?.restVials || []).map(rv => ({
    ...rv,
    batch: restVialBatches.find(b => b.id === rv.batchId),
    internalId: restVials.find(r => r.id === rv.restVialId)?.internalId || 0,
  }))

  const returnEntries: CleanroomReturnEntry[] = sequenceOrder?.returns || []

  const flatBatches = [
    ...(batches.activeSubstances || []),
    ...(batches.carrier || []),
    ...(batches.consumables || []),
    ...(batches.solvents || []),
  ]

  return {
    sequenceOrder,
    batches,
    restVialEntries,
    returnEntries,
    flatBatches,
  }
}

export type CleanroomSequenceOrderData = ReturnType<typeof useCleanroomSequenceBatches>
export type CleanroomSequenceOrderBatches = CleanroomSequenceOrderData['batches']
export type CleanroomSequenceOrderFlatBatches = CleanroomSequenceOrderData['flatBatches']
